import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, ArrowRight, AlertTriangle } from "lucide-react";
import ProductPicker from "@/components/ProductPicker";
import { formatDOP } from "@/lib/format";
import { EMPTY_CONCEPT, LINE_KINDS } from "./purchaseConfig";

const num = "h-8 font-mono text-right";

export function InventoryLines({ lines, setLines, updateCosts }) {
  const upd = (i, patch) => setLines((a) => a.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const add = (p) => setLines((arr) => {
    const ex = arr.find((x) => x.product_id === p.id);
    if (ex) return arr.map((x) => (x.product_id === p.id ? { ...x, quantity: Number(x.quantity) + 1 } : x));
    return [...arr, { product_id: p.id, sku: p.sku, name: p.name, quantity: 1, price: p.cost || 0, prev_cost: p.cost || 0,
      sale_price: p.price || 0, itbis_applies: p.itbis_applies, kind: "bien" }];
  });
  return (
    <div className="space-y-2">
      <ProductPicker onSelect={add} showCost onlyActive={false} testidPrefix="purchase-picker" placeholder="Agregar producto: SKU, nombre o código de barras" pageSize={8} />
      <div className="border border-border rounded-md overflow-hidden" data-testid="purchase-lines">
        <div className="grid grid-cols-12 gap-2 px-2 py-1.5 bg-muted/50 overline text-[10px]">
          <div className="col-span-5">Producto</div><div className="col-span-2 text-right">Cant.</div>
          <div className="col-span-2 text-right">Costo unit.</div><div className="col-span-2 text-right">Subtotal</div><div />
        </div>
        {lines.length === 0 && <div className="p-4 text-sm text-muted-foreground text-center">Sin líneas</div>}
        {lines.map((l, i) => {
          const changed = Number(l.price) !== Number(l.prev_cost);
          const overPrice = l.sale_price > 0 && Number(l.price) >= Number(l.sale_price);
          return (
            <div key={l.product_id} className="grid grid-cols-12 gap-2 items-center px-2 py-1.5 border-t border-border/60 text-sm">
              <div className="col-span-5 min-w-0">
                <div className="truncate font-semibold">{l.name}</div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid={`purchase-line-cost-hint-${i}`}>
                  {updateCosts && changed ? (<>Costo {formatDOP(l.prev_cost)} <ArrowRight className="w-3 h-3" /> <b className="text-primary">{formatDOP(l.price)}</b></>)
                    : updateCosts ? "Costo de la ficha sin cambio" : "La ficha del producto no cambia"}
                </div>
                {overPrice && <div className="text-[11px] text-destructive flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Costo ≥ precio de venta ({formatDOP(l.sale_price)})</div>}
              </div>
              <Input type="number" min="0" step="1" className={`col-span-2 ${num}`} value={l.quantity} onChange={(e) => upd(i, { quantity: e.target.value })} data-testid={`purchase-line-qty-${i}`} />
              <Input type="number" min="0" step="0.01" className={`col-span-2 ${num}`} value={l.price} onChange={(e) => upd(i, { price: e.target.value })} data-testid={`purchase-line-cost-${i}`} />
              <div className="col-span-2 text-right font-mono text-xs">{formatDOP(Number(l.quantity || 0) * Number(l.price || 0))}</div>
              <button type="button" className="justify-self-end" onClick={() => setLines((a) => a.filter((_, j) => j !== i))} data-testid={`purchase-line-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ConceptLines({ lines, setLines, categories }) {
  const upd = (i, patch) => setLines((a) => a.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  return (
    <div className="space-y-2" data-testid="purchase-concept-lines">
      {lines.map((l, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center border border-border rounded-md p-2">
          <Input className="col-span-12 md:col-span-4 h-8" placeholder="Concepto (ej. Reparación de techo)" value={l.name} onChange={(e) => upd(i, { name: e.target.value })} data-testid={`purchase-concept-name-${i}`} />
          <select className="col-span-6 md:col-span-2 h-8 rounded-md border border-input bg-transparent px-2 text-xs" value={l.kind}
            onChange={(e) => upd(i, { kind: e.target.value })} title={LINE_KINDS.find((k) => k.value === l.kind)?.hint} data-testid={`purchase-concept-kind-${i}`}>
            {LINE_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
          <select className="col-span-6 md:col-span-2 h-8 rounded-md border border-input bg-transparent px-2 text-xs" value={l.category}
            onChange={(e) => upd(i, { category: e.target.value })} data-testid={`purchase-concept-cat-${i}`}>
            <option value="">Otros gastos</option>
            {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          <Input type="number" min="0" className={`col-span-3 md:col-span-1 ${num}`} value={l.quantity} onChange={(e) => upd(i, { quantity: e.target.value })} data-testid={`purchase-concept-qty-${i}`} />
          <Input type="number" min="0" step="0.01" className={`col-span-4 md:col-span-2 ${num}`} value={l.price} onChange={(e) => upd(i, { price: e.target.value })} data-testid={`purchase-concept-price-${i}`} />
          <div className="col-span-5 md:col-span-1 flex items-center justify-end gap-2">
            <label className="flex items-center gap-1 text-[11px]"><Checkbox checked={l.itbis_applies} onCheckedChange={(v) => upd(i, { itbis_applies: !!v })} data-testid={`purchase-concept-itbis-${i}`} />ITBIS</label>
            <button type="button" disabled={lines.length === 1} onClick={() => setLines((a) => a.filter((_, j) => j !== i))} data-testid={`purchase-concept-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => setLines((a) => [...a, { ...EMPTY_CONCEPT }])} data-testid="purchase-concept-add"><Plus className="w-4 h-4 mr-1" /> Agregar línea</Button>
    </div>
  );
}
